// ---------------------------------------------------------------------------
// Client-side validation utilities for Client Detail (module 7.13)
// ---------------------------------------------------------------------------

/**
 * Validate IBAN using mod-97 checksum (ISO 7064).
 * Returns true if the IBAN checksum is valid.
 * Country-specific length rules are validated server-side.
 */
export function validateIBAN(iban: string): boolean {
  // Remove spaces and convert to uppercase
  const cleaned = iban.replace(/\s+/g, '').toUpperCase();

  // Minimum IBAN length is 15 characters
  if (cleaned.length < 15 || cleaned.length > 34) return false;

  // Must start with 2 letters followed by 2 digits
  if (!/^[A-Z]{2}\d{2}/.test(cleaned)) return false;

  // Rearrange: move first 4 chars to end
  const rearranged = cleaned.slice(4) + cleaned.slice(0, 4);

  // Convert letters to numbers (A=10, B=11, ..., Z=35)
  const numeric = rearranged.replace(/[A-Z]/g, (ch) => String(ch.charCodeAt(0) - 55));

  // Mod-97 check (handle large numbers by processing in chunks)
  let remainder = 0;
  for (let i = 0; i < numeric.length; i++) {
    remainder = (remainder * 10 + Number(numeric[i])) % 97;
  }

  return remainder === 1;
}

/**
 * Validate BIC/SWIFT code format.
 * Must be 8 or 11 alphanumeric characters.
 */
export function validateBIC(bic: string): boolean {
  const cleaned = bic.replace(/\s+/g, '').toUpperCase();
  return /^[A-Z0-9]{8}$|^[A-Z0-9]{11}$/.test(cleaned);
}

/**
 * Validate email format.
 */
export function validateEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

/**
 * Validate URL format (must be a valid http:// or https:// URL).
 */
export function validateUrl(url: string): boolean {
  try {
    const parsed = new URL(url.trim());
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * Format a decimal-string amount for display. The stored value is
 * numeric(20,4); the display uses the currency's conventional minor-unit
 * count (2 for USD/EUR/GBP, 0 for JPY, etc.). Separating storage precision
 * from display precision keeps storage and operator-facing money display
 * layered correctly.
 */
export function formatMonetary(value: string | null | undefined, currency?: string): string {
  if (value === undefined || value === null) return '—';
  const trimmed = String(value).trim();
  if (!/^-?\d+(\.\d+)?$/.test(trimmed)) return '—';

  const num = Number(trimmed);
  if (!Number.isFinite(num)) return '—';

  const normalizedCurrency = currency?.trim().toUpperCase();
  if (normalizedCurrency) {
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: normalizedCurrency,
      }).format(num);
    } catch {
      const formatted = num.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 4,
      });
      return `${formatted} ${normalizedCurrency}`;
    }
  }

  return num.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  });
}
